import React, { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X, Ban, RotateCcw, Clock, CheckCircle2, CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

const isValidHHMM = (s) => /^([01]\d|2[0-3]):[0-5]\d$/.test(s || "");

export const GroomingSlotsDialog = ({ open, onOpenChange }) => {
  const { t } = useLang();
  const s = t.admin.slots;
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [data, setData] = useState(null); // {available, all_slots, base, added, blocked, booked}
  const [overrides, setOverrides] = useState([]);
  const [newTime, setNewTime] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (d) => {
    setBusy(true);
    try {
      const [avail, ovs] = await Promise.all([
        api.get(`/grooming/availability?date=${d}`),
        api.get(`/admin/grooming/overrides?date=${d}`),
      ]);
      setData(avail.data);
      setOverrides(ovs.data);
    } catch {
      toast.error(s.load_error);
    } finally {
      setBusy(false);
    }
  }, [s.load_error]);

  useEffect(() => {
    if (open && date) load(date);
  }, [open, date, load]);

  const addOverride = async (time, action) => {
    try {
      await api.post("/admin/grooming/overrides", { date, time, action });
      await load(date);
      toast.success(s.saved);
    } catch {
      toast.error(s.save_error);
    }
  };

  const removeOverride = async (time, action) => {
    const ov = overrides.find((o) => o.time === time && o.action === action);
    if (!ov) return;
    try {
      await api.delete(`/admin/grooming/overrides/${ov.id}`);
      await load(date);
      toast.success(s.saved);
    } catch {
      toast.error(s.save_error);
    }
  };

  const addCustomSlot = async () => {
    if (!isValidHHMM(newTime)) {
      toast.error(s.invalid_time);
      return;
    }
    await addOverride(newTime, "add");
    setNewTime("");
  };

  const isBlocked = (time) => data?.blocked?.includes(time);
  const isBooked = (time) => data?.booked?.includes(time);
  const isAdded = (time) => data?.added?.includes(time);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-brand-cream max-w-lg rounded-[1.75rem] p-0 overflow-y-auto max-h-[92vh]" data-testid="grooming-slots-dialog">
        <div className="bg-brand text-brand-cream px-6 pt-6 pb-5">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-cream flex items-center gap-2">
              <Clock className="h-5 w-5" /> {s.title}
            </DialogTitle>
            <DialogDescription className="text-brand-cream/80">{s.subtitle}</DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 space-y-5">
          {/* Date picker */}
          <div>
            <Label className="text-brand-ink font-semibold flex items-center gap-1.5">
              <CalendarIcon className="h-4 w-4 text-brand" /> {s.date_label}
            </Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-xl border-brand/20 bg-white focus-visible:ring-brand h-11 mt-1"
              data-testid="slots-date-input"
            />
          </div>

          {/* Base slots */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/60 mb-2">{s.base_label}</p>
            <div className="grid grid-cols-3 gap-2" data-testid="base-slots">
              {["11:00", "14:00", "17:00"].map((tm) => {
                const blocked = isBlocked(tm);
                const booked = isBooked(tm);
                return (
                  <div
                    key={tm}
                    className={`rounded-xl border p-3 text-center transition-colors ${
                      booked
                        ? "border-amber-300 bg-amber-50"
                        : blocked
                        ? "border-rose-200 bg-rose-50"
                        : "border-bamboo/30 bg-bamboo-soft/40"
                    }`}
                  >
                    <p className="font-heading text-lg font-bold text-brand-ink">{tm}</p>
                    <p className={`text-[11px] font-bold uppercase tracking-wide mt-0.5 ${
                      booked ? "text-amber-700" : blocked ? "text-rose-600" : "text-bamboo-deep"
                    }`}>
                      {booked ? s.status_booked : blocked ? s.status_blocked : s.status_open}
                    </p>
                    {!booked && (
                      blocked ? (
                        <Button
                          onClick={() => removeOverride(tm, "block")}
                          size="sm"
                          className="mt-2 w-full h-8 rounded-full bg-brand hover:bg-brand-hover text-brand-cream text-xs font-bold"
                          data-testid={`unblock-${tm}`}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" /> {s.unblock}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => addOverride(tm, "block")}
                          size="sm"
                          variant="outline"
                          className="mt-2 w-full h-8 rounded-full border-rose-300 text-rose-600 hover:bg-rose-50 text-xs font-bold"
                          data-testid={`block-${tm}`}
                        >
                          <Ban className="h-3 w-3 mr-1" /> {s.block}
                        </Button>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Custom / added slots */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/60 mb-2">{s.added_label}</p>
            {data?.added?.length ? (
              <div className="flex flex-wrap gap-2" data-testid="added-slots">
                {data.added.map((tm) => {
                  const booked = isBooked(tm);
                  return (
                    <span
                      key={tm}
                      className={`inline-flex items-center gap-1.5 rounded-full pl-3 pr-1 py-1 text-sm font-bold border ${
                        booked ? "border-amber-300 bg-amber-50 text-amber-800" : "border-bamboo bg-bamboo-soft text-bamboo-deep"
                      }`}
                    >
                      {tm} {booked && <span className="text-[10px] uppercase">· {s.status_booked}</span>}
                      {!booked && (
                        <button
                          onClick={() => removeOverride(tm, "add")}
                          className="ml-1 grid place-items-center h-6 w-6 rounded-full hover:bg-rose-100 text-rose-600 transition-colors"
                          aria-label={s.remove}
                          data-testid={`remove-added-${tm}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-brand-ink/50 italic">{s.no_added}</p>
            )}

            <div className="mt-3 flex gap-2">
              <Input
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                placeholder="HH:MM"
                className="rounded-full border-brand/20 bg-white focus-visible:ring-brand h-10"
                data-testid="new-slot-input"
              />
              <Button
                onClick={addCustomSlot}
                disabled={!newTime}
                className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-10 px-5 shrink-0"
                data-testid="add-slot-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> {s.add}
              </Button>
            </div>
          </div>

          {/* Summary */}
          {data && (
            <div className="rounded-2xl bg-white border border-brand/10 p-4 text-sm" data-testid="slots-summary">
              <p className="font-bold text-brand-ink mb-2 flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-bamboo" /> {s.summary_label}
              </p>
              {data.available.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {data.available.map((tm) => (
                    <span key={tm} className="rounded-full bg-bamboo-soft text-bamboo-deep text-xs font-bold px-2.5 py-1">{tm}</span>
                  ))}
                </div>
              ) : (
                <p className="text-brand-ink/60 italic">{s.no_available}</p>
              )}
            </div>
          )}

          <Button
            onClick={() => onOpenChange(false)}
            className="w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-11"
            data-testid="slots-close-btn"
          >
            {s.close}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GroomingSlotsDialog;
